// Settings — admin's own profile (email read-only) + 2FA management
// + system config viewer.
//
// 2FA enrollment runs in a non-dismissible modal wizard
// (EnrollMFAModal). Trying to inline-edit the secret-input form
// inside the settings page led to admins navigating away mid-flow,
// leaving PENDING_VERIFICATION rows dangling and no clear cleanup
// path. The modal forces a single outcome per started enrollment.
import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import {
  fetchMe, fetchMyFactors, revokeMFA,
  fetchConfigs, setConfig, deleteConfig, keys,
} from "@/lib/queries";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Pagination } from "@/components/ui/Pagination";
import { SortableHeader } from "@/components/ui/SortableHeader";
import { ConfirmModal } from "@/components/ui/ConfirmModal";
import { useTableState } from "@/lib/useTableState";
import { fmtDate, decodeConfigValue, statusLabel } from "@/lib/format";
import { EnrollMFAModal } from "@/components/mfa/EnrollMFAModal";
import { ChangePasswordForm } from "@/components/security/ChangePasswordForm";
import type { MFAFactor, SystemConfigItem } from "@/lib/types";

export const Route = createFileRoute("/_app/settings")({
  loader: ({ context }) =>
    context.queryClient.ensureQueryData({ queryKey: keys.myFactors(), queryFn: fetchMyFactors }),
  component: SettingsPage,
});

function SettingsPage() {
  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-2xl tracking-tight">Settings</h1>
        <p className="text-sm text-muted">Account, password & 2FA, system configuration.</p>
      </div>
      <ProfileCard />
      <PasswordSection />
      <MFASection />
      <SystemConfigSection />
    </div>
  );
}

function ProfileCard() {
  const me = useQuery({ queryKey: keys.me(), queryFn: fetchMe });
  return (
    <Card>
      <CardHeader className="border-b border-border"><CardTitle>Account</CardTitle></CardHeader>
      <CardBody className="space-y-2 pt-5 text-sm">
        <div><span className="text-2xs uppercase tracking-wide text-muted">Email</span><div>{me.data?.email}</div></div>
        <div><span className="text-2xs uppercase tracking-wide text-muted">Role</span><div>{me.data?.role}</div></div>
        <div><span className="text-2xs uppercase tracking-wide text-muted">Session expires</span><div>{fmtDate(me.data?.expires_at)}</div></div>
      </CardBody>
    </Card>
  );
}

// Password (SM-536). Ahead of 2FA deliberately: until this shipped no
// non-Super-Admin could change their own password at all, and the
// account recovery path ran through an engineer with prod database
// access. It is the first thing an admin should be able to do here.
function PasswordSection() {
  return (
    <Card>
      <CardHeader className="border-b border-border"><CardTitle>Password</CardTitle></CardHeader>
      <CardBody className="max-w-md pt-5">
        <ChangePasswordForm />
      </CardBody>
    </Card>
  );
}

function MFASection() {
  const qc = useQueryClient();
  const factors = useQuery({ queryKey: keys.myFactors(), queryFn: fetchMyFactors });
  const [enrollOpen, setEnrollOpen] = useState(false);
  const [pending, setPending] = useState<MFAFactor | null>(null);

  const revoke = useMutation({
    mutationFn: (id: string) => revokeMFA(id),
    onSuccess: () => {
      setPending(null);
      qc.invalidateQueries({ queryKey: keys.myFactors() });
    },
    meta: { successMessage: "Two-factor factor removed." },
  });

  return (
    <Card>
      <CardHeader className="border-b border-border">
        <CardTitle>Two-factor authentication</CardTitle>
        <Button size="sm" onClick={() => setEnrollOpen(true)}>Enroll new TOTP</Button>
      </CardHeader>
      <CardBody className="space-y-4 pt-5">
        <ul className="divide-y divide-border/60">
          {(factors.data ?? []).map((f) => (
            <li key={f.factor_id} className="flex items-center justify-between px-2 py-3">
              <div>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium">{f.friendly_name || f.kind}</span>
                  <Badge tone={f.status === "ACTIVE" ? "success" : f.status === "PENDING_VERIFICATION" ? "warning" : "muted"}>{statusLabel(f.status)}</Badge>
                  <Badge tone="muted">{f.kind}</Badge>
                </div>
                <div className="mt-0.5 text-2xs text-muted">
                  Added {fmtDate(f.registered_at)}{f.last_used_at && ` · last used ${fmtDate(f.last_used_at)}`}
                </div>
              </div>
              {f.status !== "REVOKED" && (
                <Button size="sm" variant="ghost" className="text-danger hover:bg-danger/10"
                  onClick={() => setPending(f)}>Revoke</Button>
              )}
            </li>
          ))}
          {(!factors.data || factors.data.length === 0) && (
            <li className="px-2 py-6 text-center text-2xs text-muted">No factors yet — enroll TOTP to lock down login.</li>
          )}
        </ul>
      </CardBody>
      {enrollOpen && <EnrollMFAModal onClose={() => setEnrollOpen(false)} />}
      <ConfirmModal
        open={pending !== null}
        tone="danger"
        title="Revoke this MFA factor?"
        description={
          <>
            <p>
              Removes <span className="font-mono text-2xs text-t-2">{pending?.friendly_name || pending?.kind}</span>
              {" "}from your account. If this is your only active factor, your next sign-in will fall back to single-factor.
            </p>
            <p className="mt-2 text-2xs">Re-enrolling later requires the original secret material — you cannot recover a revoked factor.</p>
          </>
        }
        confirmLabel="Revoke factor"
        cancelLabel="Keep factor"
        loading={revoke.isPending}
        error={revoke.isError ? (revoke.error as Error).message : null}
        onConfirm={() => pending && revoke.mutate(pending.factor_id)}
        onCancel={() => { revoke.reset(); setPending(null); }}
      />
    </Card>
  );
}

function SystemConfigSection() {
  const qc = useQueryClient();
  // System configs is a small list — sort + paginate, no filter UI.
  const table = useTableState({
    defaultSort: { by: "key", dir: "asc" },
  });
  const configs = useQuery({
    queryKey: keys.configsList(table.limit, table.offset, table.sort),
    queryFn: () =>
      fetchConfigs({ limit: table.limit, offset: table.offset, sort: table.sort }),
  });
  const [key, setKey] = useState("");
  const [value, setValue] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const [pendingDel, setPendingDel] = useState<SystemConfigItem | null>(null);

  const setMut = useMutation({
    mutationFn: () => {
      let parsed: unknown;
      try { parsed = JSON.parse(value); } catch { parsed = value; }
      return setConfig(key, parsed);
    },
    onSuccess: async () => {
      setErr(null); setKey(""); setValue("");
      await qc.invalidateQueries({ queryKey: keys.configs() });
    },
    onError: (e) => setErr((e as Error).message),
    meta: { successMessage: "Configuration saved." },
  });
  const del = useMutation({
    mutationFn: (k: string) => deleteConfig(k),
    onSuccess: () => {
      setPendingDel(null);
      qc.invalidateQueries({ queryKey: keys.configs() });
    },
    meta: { successMessage: "Configuration deleted." },
  });

  return (
    <Card>
      <CardHeader className="border-b border-border">
        <CardTitle>System configuration</CardTitle>
        <span className="text-2xs text-muted">{configs.data?.page.total ?? 0} keys</span>
      </CardHeader>
      <CardBody className="space-y-4 pt-5">
        <div className="grid gap-2 sm:grid-cols-[1fr_2fr_auto] sm:items-end">
          <Input label="Key" value={key} onChange={(e) => setKey(e.target.value)} placeholder="maintenance.banner" />
          <Input label="Value (JSON)" value={value} onChange={(e) => setValue(e.target.value)} placeholder='{"enabled": false}' />
          <Button onClick={() => setMut.mutate()} loading={setMut.isPending} disabled={!key || !value}>Set</Button>
        </div>
        {err && <p className="rounded bg-danger/10 px-3 py-2 text-xs text-danger">{err}</p>}
      </CardBody>
      <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="border-y border-border bg-surface-2">
          <tr>
            <SortableHeader label="Key" field="key" sort={table.sort} onSortChange={table.setSort} className="px-5" />
            <th className="px-3 py-3 text-left text-2xs font-medium uppercase tracking-wide text-muted">Value</th>
            <SortableHeader label="Updated" field="updated_at" sort={table.sort} onSortChange={table.setSort} align="right" />
            <th className="px-5 py-3 text-right text-2xs font-medium uppercase tracking-wide text-muted">Actions</th>
          </tr>
        </thead>
        <tbody>
          {(configs.data?.items ?? []).map((c) => (
            <tr key={c.key} className="border-b border-border/60 last:border-0">
              <td className="px-5 py-2.5 font-mono text-xs">{c.key}</td>
              <td className="px-3 py-2.5 font-mono text-2xs text-muted truncate max-w-md">{JSON.stringify(decodeConfigValue(c.value))}</td>
              <td className="px-3 py-2.5 text-right text-2xs text-muted">{fmtDate(c.updated_at)}</td>
              <td className="px-5 py-2.5 text-right">
                <Button size="sm" variant="ghost" className="text-danger hover:bg-danger/10"
                  onClick={() => setPendingDel(c)}>Delete</Button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>
      {configs.data && (
        <Pagination
          page={configs.data.page}
          currentPage={table.page}
          onPageChange={table.setPage}
          itemLabel="keys"
        />
      )}
      <ConfirmModal
        open={pendingDel !== null}
        tone="danger"
        title="Delete this system config key?"
        description={
          <>
            <p>
              Removes <span className="font-mono text-2xs text-t-2">{pendingDel?.key}</span> from the live config store.
              Any service reading this key will fall back to its compiled default on next refresh.
            </p>
            <p className="mt-2 text-2xs">This cannot be undone — you'll need to re-create the key with the original value to roll back.</p>
          </>
        }
        confirmLabel="Delete key"
        cancelLabel="Keep key"
        reasonLabel="Reason (audit log)"
        loading={del.isPending}
        error={del.isError ? (del.error as Error).message : null}
        onConfirm={() => pendingDel && del.mutate(pendingDel.key)}
        onCancel={() => { del.reset(); setPendingDel(null); }}
      />
    </Card>
  );
}
